import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../../components/Navbar';
import Footer from '../../../home/components/Footer';

export default function ElectricalInstallationsPage() {
  useEffect(() => {
    document.title = 'Electrical Installations Watford | Beyond Electrical';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional electrical installations in Watford. NICEIC approved electricians for all types of electrical work. Call 020 3198 1818 for a free quote.');
    }

    const schema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Beyond Electrical Services",
      "image": "https://beyondelectrical.co.uk/logo.png",
      "telephone": "020 3198 1818",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Watford",
        "addressLocality": "Watford",
        "addressRegion": "Hertfordshire",
        "postalCode": "WD17",
        "addressCountry": "GB"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 51.6565,
        "longitude": -0.3957
      },
      "url": "https://beyondelectrical.co.uk/services/installations/electrical-installations",
      "areaServed": "Watford",
      "serviceType": "Electrical Installations",
      "openingHours": "Mo-Su 00:00-23:59"
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628] via-[#0F2847] to-[#0A1628]"></div>
        <div className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/75cb27796d86244437251737ef7c11fb.jpg')] bg-cover bg-center opacity-10"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="flex flex-wrap items-center justify-center gap-3 mb-6">
            <div className="flex items-center gap-2 bg-[#FFD700]/20 border border-[#FFD700] text-[#FFD700] px-4 py-2 rounded-full text-sm font-semibold">
              <i className="ri-shield-check-fill"></i>
              NICEIC Certified
            </div>
            <div className="flex items-center gap-2 bg-[#FFD700]/20 border border-[#FFD700] text-[#FFD700] px-4 py-2 rounded-full text-sm font-semibold">
              <i className="ri-map-pin-fill"></i>
              Watford Electricians
            </div>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Electrical Installations<br />
            <span className="text-[#FFD700]">in Watford</span>
          </h1>

          <p className="text-lg sm:text-xl text-gray-200 mb-8 max-w-3xl mx-auto leading-relaxed">
            Complete electrical installation services for new builds, extensions, and renovations throughout Watford, Rickmansworth, and surrounding areas. Professional, certified electricians delivering safe, reliable electrical systems.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:02031981818"
              className="w-full sm:w-auto bg-[#FFD700] text-[#0A1628] px-8 py-4 rounded-full font-bold text-lg hover:bg-[#FFC700] transition-all duration-300 shadow-xl shadow-[#FFD700]/30 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill"></i>
              020 3198 1818
            </a>
            <a
              href="/#contact"
              className="w-full sm:w-auto bg-white/10 backdrop-blur-sm border-2 border-white text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-[#0A1628] transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-fill"></i>
              Free Quote
            </a>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A1628] mb-4">
              What's Included in Our Electrical Installations
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive electrical installation services for residential and commercial properties in Watford
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#FFD700] rounded-xl text-2xl flex-shrink-0">
                  🏗️
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-2">New Build Installations</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Complete electrical systems for new construction projects, from first fix to final connections, ensuring compliance with all building regulations in Watford.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#FFD700] rounded-xl text-2xl flex-shrink-0">
                  🏠
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-2">Home Extensions</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Electrical installations for extensions and loft conversions, seamlessly integrated with your existing electrical system throughout Watford homes.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#FFD700] rounded-xl text-2xl flex-shrink-0">
                  🔌
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-2">Complete Wiring Systems</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Full electrical wiring installations including power circuits, lighting circuits, and dedicated appliance circuits for Watford properties.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#FFD700] rounded-xl text-2xl flex-shrink-0">
                  🔧
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-2">Consumer Unit Installation</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Modern consumer units with RCD protection and circuit breakers, installed to the latest 18th Edition regulations in Watford.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#FFD700] rounded-xl text-2xl flex-shrink-0">
                  💡
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-2">Lighting Circuits</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Complete lighting installation including switches, dimmers, and modern LED lighting solutions for homes and businesses in Watford.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#FFD700] rounded-xl text-2xl flex-shrink-0">
                  🏢
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-2">Commercial Installations</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Electrical installations for offices, shops, and commercial premises throughout Watford, meeting all commercial electrical standards.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A1628] mb-4">
              Benefits of Professional Electrical Installation
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Why choose Beyond Electrical for your installation project in Watford
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#FFD700] to-[#FFC700] rounded-2xl text-4xl mx-auto mb-4">
                ✅
              </div>
              <h3 className="text-xl font-bold text-[#0A1628] mb-3">Safety Guaranteed</h3>
              <p className="text-gray-600 leading-relaxed">
                All installations comply with BS 7671 18th Edition wiring regulations, ensuring maximum safety for your Watford property.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#FFD700] to-[#FFC700] rounded-2xl text-4xl mx-auto mb-4">
                📋
              </div>
              <h3 className="text-xl font-bold text-[#0A1628] mb-3">Full Certification</h3>
              <p className="text-gray-600 leading-relaxed">
                NICEIC certification and electrical installation certificates provided for all work completed in Watford and surrounding areas.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#FFD700] to-[#FFC700] rounded-2xl text-4xl mx-auto mb-4">
                ⚡
              </div>
              <h3 className="text-xl font-bold text-[#0A1628] mb-3">Future-Proof Design</h3>
              <p className="text-gray-600 leading-relaxed">
                Modern electrical systems designed to accommodate future upgrades and smart home technology in your Watford home.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#FFD700] to-[#FFC700] rounded-2xl text-4xl mx-auto mb-4">
                💰
              </div>
              <h3 className="text-xl font-bold text-[#0A1628] mb-3">Energy Efficient</h3>
              <p className="text-gray-600 leading-relaxed">
                Optimized electrical installations that reduce energy consumption and lower electricity bills for Watford homeowners.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#FFD700] to-[#FFC700] rounded-2xl text-4xl mx-auto mb-4">
                🏆
              </div>
              <h3 className="text-xl font-bold text-[#0A1628] mb-3">15 Years Experience</h3>
              <p className="text-gray-600 leading-relaxed">
                Over 15 years of electrical installation experience serving homes and businesses throughout Watford and Rickmansworth.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#FFD700] to-[#FFC700] rounded-2xl text-4xl mx-auto mb-4">
                🛡️
              </div>
              <h3 className="text-xl font-bold text-[#0A1628] mb-3">Fully Insured</h3>
              <p className="text-gray-600 leading-relaxed">
                Comprehensive public liability insurance and professional indemnity cover for complete peace of mind in Watford.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0A1628] to-[#0F2847]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Why Choose Beyond Electrical in Watford?
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Your trusted local electricians for electrical installations in Watford
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white/5 backdrop-blur-sm border border-[#FFD700]/30 rounded-2xl p-6 text-center">
              <div className="text-4xl mb-3">🏆</div>
              <h3 className="text-xl font-bold text-white mb-2">15 Years Experience</h3>
              <p className="text-gray-300 text-sm">
                Serving Watford since 2009
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-[#FFD700]/30 rounded-2xl p-6 text-center">
              <div className="text-4xl mb-3">✅</div>
              <h3 className="text-xl font-bold text-white mb-2">NICEIC Approved</h3>
              <p className="text-gray-300 text-sm">
                Fully certified electricians
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-[#FFD700]/30 rounded-2xl p-6 text-center">
              <div className="text-4xl mb-3">📍</div>
              <h3 className="text-xl font-bold text-white mb-2">Local to Watford</h3>
              <p className="text-gray-300 text-sm">
                Fast response times
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-[#FFD700]/30 rounded-2xl p-6 text-center">
              <div className="text-4xl mb-3">💯</div>
              <h3 className="text-xl font-bold text-white mb-2">100% Satisfaction</h3>
              <p className="text-gray-300 text-sm">
                Quality guaranteed
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Areas Covered */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0A1628] mb-4">
            Electrical Installation Services Across Hertfordshire &amp; Middlesex
          </h2>
          <p className="text-lg text-gray-600 mb-10 max-w-3xl mx-auto">
            We provide professional electrical installations throughout Watford and all surrounding areas. Click your location for dedicated local information.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {[
              { name: 'Rickmansworth', slug: 'rickmansworth' },
              { name: 'Chorleywood', slug: 'chorleywood' },
              { name: 'Moor Park & Northwood', slug: 'moor-park-northwood' },
              { name: 'Croxley Green', slug: 'croxley-green' },
              { name: 'Pinner', slug: 'pinner' },
              { name: 'Bushey', slug: 'bushey' },
              { name: 'Abbots Langley', slug: 'abbots-langley' },
              { name: 'Harrow', slug: 'harrow' },
              { name: 'Ruislip', slug: 'ruislip' },
              { name: 'Stanmore', slug: 'stanmore' },
              { name: 'Edgware', slug: 'edgware' },
              { name: 'Mill Hill', slug: 'mill-hill' },
              { name: 'Radlett', slug: 'radlett' },
              { name: 'Borehamwood', slug: 'borehamwood' },
              { name: 'Elstree', slug: 'elstree' },
              { name: 'Potters Bar', slug: 'potters-bar' },
              { name: 'South Oxhey', slug: 'south-oxhey' },
            ].map((area) => (
              <Link
                key={area.slug}
                to={`/services/installations/electrical-installations/${area.slug}`}
                className="group flex items-center justify-between bg-gradient-to-r from-gray-50 to-white border border-gray-200 hover:border-[#FFD700] hover:bg-gradient-to-r hover:from-[#FFD700]/10 hover:to-[#FFC700]/5 rounded-xl px-4 py-3 transition-all duration-200 cursor-pointer"
              >
                <span className="text-sm font-semibold text-[#0A1628] group-hover:text-[#0A1628]">{area.name}</span>
                <i className="ri-arrow-right-s-line text-gray-400 group-hover:text-[#FFD700] transition-colors duration-200"></i>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0A1628] via-[#0F2847] to-[#0A1628]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Need Electrical Installation in Watford?
          </h2>
          <p className="text-lg sm:text-xl text-gray-300 mb-10 leading-relaxed">
            Contact Beyond Electrical today for a free, no-obligation quote. Our expert electricians are ready to help with your electrical installation project in Watford.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:02031981818"
              className="w-full sm:w-auto bg-[#FFD700] text-[#0A1628] px-10 py-5 rounded-full font-bold text-lg hover:bg-[#FFC700] transition-all duration-300 shadow-2xl shadow-[#FFD700]/40 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill text-xl"></i>
              Call 020 3198 1818
            </a>
            <a
              href="/#contact"
              className="w-full sm:w-auto bg-white/10 backdrop-blur-sm border-2 border-white text-white px-10 py-5 rounded-full font-bold text-lg hover:bg-white hover:text-[#0A1628] transition-all duration-300 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-fill text-xl"></i>
              Get Free Quote
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}